<template lang="pug">
div
  img.canvas-preview(v-if="compact && canvasUrl" :src="canvasUrl" draggable="false")
  iframe(v-else-if="!compact && pdfBlob" :src="pdfBlob" frameborder="0")
</template>

<script>
import { debounce } from 'lodash'
import { mapGetters } from 'vuex'
import pdfRenderer from '../../services/pdf_renderer'
import * as pdfjsLib from 'pdfjs-dist'

pdfjsLib.GlobalWorkerOptions.workerSrc = new URL(
  'pdfjs-dist/build/pdf.worker.min.mjs',
  import.meta.url
).href

const RENDER_DELAY_MS = 600
const CANVAS_SCALE = 3

export default {
  props: ["game", "component", "item", "compact", "renderPriority", "autoRefreshLayers", "paused"],

  created() {
    this._renderToken = 0
    this._cancelRender = null  // cancel handle for the currently queued render
    this._pendingRender = false // true when a render was requested but not yet completed
    this._renderPDF = debounce(async () => {
      if (this.game && this.component && this.item && this.componentTemplate) {
        const token = ++this._renderToken
        const renderPromise = pdfRenderer.renderItemToPdf(this.game, this.component, this.item, this.componentTemplate, this.renderPriority || 0)
        this._cancelRender = renderPromise.cancel
        const pdf = await renderPromise
        this._cancelRender = null
        // null = cancelled by pause; stale token = superseded by a newer render
        if (pdf === null || token !== this._renderToken) return
        this._pendingRender = false
        this.pdfBlob = pdf
        if (this.compact) {
          this.canvasUrl = await this._renderToCanvas(pdf)
          if (token !== this._renderToken) return
        }
        this.$emit('rendered')
      }
    }, RENDER_DELAY_MS)
  },

  mounted() { this.renderPDF() },

  beforeDestroy() {
    this._renderPDF.cancel()
    if (this._cancelRender) this._cancelRender()
  },

  data() {
    return { pdfBlob: null, canvasUrl: null }
  },

  computed: {
    ...mapGetters([
      "activeDimensions",
      "activeLayer",
      "findComponentTemplate",
      "findAllTemplateLayers",
      "layerHighlighting",
      "allFonts"
    ]),

    layerVisible() { return (this.activeLayer && this.activeLayer.visible ) },
    layerStrokePresent() { return (this.activeLayer && this.activeLayer.strokePresent) },
    layerStrokeWidth() { return (this.activeLayer && this.activeLayer.strokeWidth) },
    layerStrokeColor() { return (this.activeLayer && this.activeLayer.strokeColor) },
    layerFillPresent() { return (this.activeLayer && this.activeLayer.fillPresent) },
    layerFillColor() { return (this.activeLayer && this.activeLayer.fillColor) },

    layerImageNameStatic() { return (this.activeLayer && this.activeLayer.imageNameStatic) },
    layerImageId() { return (this.activeLayer && this.activeLayer.imageId) },
    layerImageNamePrefix() { return (this.activeLayer && this.activeLayer.imageNamePrefix) },
    layerImageNameProperty() { return (this.activeLayer && this.activeLayer.imageNameProperty) },
    layerImageNameSuffix() { return (this.activeLayer && this.activeLayer.imageNameSuffix) },
    layerImageScaling() { return (this.activeLayer && this.activeLayer.imageScaling) },
    layerHorizontalAlignment() { return (this.activeLayer && this.activeLayer.horizontalAlignment) },
    layerVerticalAlignment() { return (this.activeLayer && this.activeLayer.verticalAlignment) },

    layerTextContentTemplate() { return (this.activeLayer && this.activeLayer.textContentTemplate) },
    layerTextColor() { return (this.activeLayer && this.activeLayer.textColor) },
    layerTextSize() { return (this.activeLayer && this.activeLayer.textSize) },
    layerOpacity() { return (this.activeLayer && this.activeLayer.opacity) },

    componentTemplate() { return this.findComponentTemplate(this.component) },
    templateLayers() { return this.findAllTemplateLayers(this.componentTemplate) },
  },

  watch: {
    paused(isPaused) {
      if (isPaused) {
        // Cancel the debounce timer so it doesn't enter the queue while hidden.
        this._renderPDF.cancel()
        // Cancel any render already sitting in the queue.
        if (this._cancelRender) {
          this._cancelRender()
          this._cancelRender = null
        }
        // _pendingRender stays true so we retry when the tab becomes visible again.
      } else if (this._pendingRender) {
        this._renderPDF()
      }
    },

    game: "renderPDF",
    component(newComp, oldComp) {
      if (newComp?.id !== oldComp?.id) {
        this.canvasUrl = null
        this.pdfBlob = null
      }
      this.renderPDF()
    },
    item: { handler: "renderPDF", deep: true },
    compact(val) {
      if (val && this.pdfBlob && !this.canvasUrl) {
        this._renderToCanvas(this.pdfBlob).then(url => { this.canvasUrl = url })
      }
    },
    activeDimensions()          { if (this.autoRefreshLayers !== false) this.renderPDF() },
    templateLayers()            { if (this.autoRefreshLayers !== false) this.renderPDF() },
    activeLayer()               { if (this.autoRefreshLayers !== false) this.renderPDF() },
    layerStrokePresent()        { if (this.autoRefreshLayers !== false) this.renderPDF() },
    layerStrokeWidth()          { if (this.autoRefreshLayers !== false) this.renderPDF() },
    layerStrokeColor()          { if (this.autoRefreshLayers !== false) this.renderPDF() },
    layerFillPresent()          { if (this.autoRefreshLayers !== false) this.renderPDF() },
    layerFillColor()            { if (this.autoRefreshLayers !== false) this.renderPDF() },
    layerImageNameStatic()      { if (this.autoRefreshLayers !== false) this.renderPDF() },
    layerImageName()            { if (this.autoRefreshLayers !== false) this.renderPDF() },
    layerImageNamePrefix()      { if (this.autoRefreshLayers !== false) this.renderPDF() },
    layerVisible()              { if (this.autoRefreshLayers !== false) this.renderPDF() },
    layerImageNameProperty()    { if (this.autoRefreshLayers !== false) this.renderPDF() },
    layerImageNameSuffix()      { if (this.autoRefreshLayers !== false) this.renderPDF() },
    layerImageScaling()         { if (this.autoRefreshLayers !== false) this.renderPDF() },
    layerHorizontalAlignment()  { if (this.autoRefreshLayers !== false) this.renderPDF() },
    layerVerticalAlignment()    { if (this.autoRefreshLayers !== false) this.renderPDF() },
    layerTextContentTemplate()  { if (this.autoRefreshLayers !== false) this.renderPDF() },
    layerTextColor()            { if (this.autoRefreshLayers !== false) this.renderPDF() },
    layerTextSize()             { if (this.autoRefreshLayers !== false) this.renderPDF() },
    layerOpacity()              { if (this.autoRefreshLayers !== false) this.renderPDF() },
    layerHighlighting()         { if (this.autoRefreshLayers !== false) this.renderPDF() },
    allFonts()                  { if (this.autoRefreshLayers !== false) this.renderPDF() },
  },

  methods: {
    renderPDF() {
      this._pendingRender = true
      if (this.paused) return
      this._renderPDF()
    },

    async _renderToCanvas(blobUrl) {
      const loadingTask = pdfjsLib.getDocument(blobUrl)
      const pdf = await loadingTask.promise
      const page = await pdf.getPage(1)
      const viewport = page.getViewport({ scale: CANVAS_SCALE })
      const canvas = document.createElement('canvas')
      canvas.width = Math.round(viewport.width)
      canvas.height = Math.round(viewport.height)
      await page.render({ canvasContext: canvas.getContext('2d'), viewport }).promise
      return canvas.toDataURL('image/png')
    }
  }
}
</script>

<style scoped>
  .canvas-preview {
    display: block;
    width: 100%;
    height: auto;
    user-select: none;
    -webkit-user-drag: none;
  }
  iframe {
    display: block;
    width: 100%;
    min-height: 400px;
    border: 0;
  }
</style>
